import type { FlexProps } from "@qws-org/ui-kit";
import { Button, Flex, Title } from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, Pages } from "~/core";
import type { Page_Links as PageLinksData } from "~/core/types";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";

type PageLinksProps = {
  data: PageLinksData;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isControlInactive: (action: Action) => boolean;
} & FlexProps;

export const PageLinks: FC<PageLinksProps> = ({
  getUrl,
  onCallAction,
  data,
  isControlInactive,
  ...rest
}) => {
  return (
    <Flex
      flexDirection="column"
      justify="center"
      p={{ px: 15, py: 16 }}
      {...rest}
      d={{ p: { py: 64 }, ...rest.d }}
      data-testid="page-links"
    >
      {data?.title && (
        <Title
          as="h2"
          fontWeight="mobile.bold"
          size="small"
          md={{
            fontWeight: "desktop.semibold",
            fontSize: "title.desktop.large",
            lineHeight: "title.desktop.large",
            m: { mb: 24 },
          }}
          m={{ mb: 24 }}
          data-testid="page-links-title"
        >
          {data.title}
        </Title>
      )}
      <Flex gap={8} flexWrap="wrap" justify="center">
        {data.items?.map((item, idx) => {
          if (!item?.page_link_items_id?.action_text) {
            return null;
          }
          const controlAction = item?.page_link_items_id?.action;
          const action = controlAction?.action?.[0];
          let href;
          if (isExternalLink(action?.item)) {
            href = action.item.href ?? undefined;
          }
          if (isInternalLink(action?.item)) {
            href = action.item.page ? getUrl(action.item.page) : undefined;
          }
          const handlePressButton = (): void => {
            if (controlAction) {
              onCallAction(controlAction);
            }
          };
          return (
            <Button
              href={href}
              onPress={handlePressButton}
              isDisabled={
                item?.page_link_items_id?.action
                  ? isControlInactive(item.page_link_items_id.action)
                  : false
              }
              key={item?.id}
              variant="secondary"
              p={{ py: 8, px: 8 }}
              size="xsmall"
              md={{
                p: { px: 16 },
                fontSize: "button.desktop.small",
                lineHeight: "button.desktop.small",
              }}
              data-testid={`page-links-button-${idx}`}
            >
              {item?.page_link_items_id?.action_text}
            </Button>
          );
        })}
      </Flex>
    </Flex>
  );
};
